import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Boxes } from "lucide-react";
import GlassBackground from "@/shared/components/glassBackground";
import { toast } from "@/shared/components/toast";
import { confirmDialog } from "@/shared/components/dialog";
import { useAutomations } from "../hooks/useAutomations";
import { useWorkflowExecution } from "../hooks/useWorkflowExecution";
import type AutomationSummary from "../models/AutomationSummary";
import { AutomationsSidebar } from "../components/automations_sidebar";
import { AutomationsGrid } from "../components/automations_grid";
import ShareAutomationModal from "../components/share_automation_modal";
import MoveToWorkspaceModal from "@/features/workspaces/components/move_to_workspace_modal";

export default function AutomationsManagementPage() {
  const navigate = useNavigate();
  const { loading, error, getAll, remove } = useAutomations();
  const { run } = useWorkflowExecution();

  const [automations, setAutomations] = useState<AutomationSummary[]>([]);
  const [runningWorkflowId, setRunningWorkflowId] = useState<string | null>(null);
  const [deletingWorkflowId, setDeletingWorkflowId] = useState<string | null>(null);
  const [sharingAutomation, setSharingAutomation] = useState<AutomationSummary | null>(null);
  const [movingAutomation, setMovingAutomation] = useState<AutomationSummary | null>(null);

  useEffect(() => {
    let ignore = false;
    const fetch = async () => {
      const result = await getAll();
      if (!ignore && result) setAutomations(result);
    };
    fetch();
    return () => {
      ignore = true;
    };
  }, [getAll]);

  const stats = {
    total: automations.length,
    totalNodes: automations.reduce((sum, a) => sum + a.nodeCount, 0),
  };

  /**
   * Un grupo por workspace más uno final para lo que quedó sin agrupar. El backend ya devuelve el
   * workspace de cada automatización, así que agrupar acá no cuesta una llamada extra.
   */
  const groups = useMemo(() => {
    const byWorkspace = new Map<string, { name: string; items: AutomationSummary[] }>();
    const ungrouped: AutomationSummary[] = [];

    for (const automation of automations) {
      if (!automation.workspaceId) {
        ungrouped.push(automation);
        continue;
      }
      const group = byWorkspace.get(automation.workspaceId);
      if (group) group.items.push(automation);
      else
        byWorkspace.set(automation.workspaceId, {
          name: automation.workspaceName ?? "Workspace",
          items: [automation],
        });
    }

    const sorted = [...byWorkspace.entries()].sort((a, b) =>
      a[1].name.localeCompare(b[1].name, "es"),
    );

    return { sorted, ungrouped };
  }, [automations]);

  const handleRun = async (workflowId: string) => {
    setRunningWorkflowId(workflowId);
    try {
      const result = await run(workflowId);
      if (result.status === "Completed") {
        toast.success("Ejecución completada correctamente");
      } else if (result.status === "Failed") {
        toast.error("La ejecución finalizó con error");
      } else {
        toast.warning(`La ejecución finalizó con estado: ${result.status}`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al ejecutar la automatización");
    } finally {
      setRunningWorkflowId(null);
    }
  };

  const handleDelete = async (automation: AutomationSummary) => {
    const confirmed = await confirmDialog({
      title: "Eliminar automatización",
      message: `Se eliminará "${automation.name}" con todas sus versiones y su configuración (webhook, programación, estado). El historial de ejecuciones se conserva. Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar",
      cancelLabel: "Cancelar",
      variant: "danger",
    });
    if (!confirmed) return;

    setDeletingWorkflowId(automation.workflowId);
    try {
      await remove(automation.workflowId);
      setAutomations((prev) => prev.filter((a) => a.workflowId !== automation.workflowId));
    } catch {
      // useAsync ya notificó el error vía toast
    } finally {
      setDeletingWorkflowId(null);
    }
  };

  const renderGrid = (items: AutomationSummary[]) => (
    <AutomationsGrid
      automations={items}
      loading={false}
      error={null}
      runningWorkflowId={runningWorkflowId}
      deletingWorkflowId={deletingWorkflowId}
      onEdit={(workflowId) => navigate(`/admin/automations/${workflowId}`)}
      onRun={handleRun}
      onDelete={handleDelete}
      onShare={setSharingAutomation}
      onMoveToWorkspace={setMovingAutomation}
    />
  );

  return (

    <GlassBackground>
      <div className="relative flex w-full overflow-hidden">
        <AutomationsSidebar total={stats.total} totalNodes={stats.totalNodes} loading={loading} />

        <main className="flex-1 space-y-10 overflow-auto p-8">
          {loading || error || automations.length === 0 ? (
            <AutomationsGrid
              automations={automations}
              loading={loading}
              error={error}
              runningWorkflowId={runningWorkflowId}
              deletingWorkflowId={deletingWorkflowId}
              onEdit={(workflowId) => navigate(`/admin/automations/${workflowId}`)}
              onRun={handleRun}
              onDelete={handleDelete}
              onShare={setSharingAutomation}
            />
          ) : (
            <>
              {groups.sorted.map(([workspaceId, group]) => (
                <section key={workspaceId} className="space-y-4">
                  <button
                    onClick={() => navigate(`/admin/workspaces/${workspaceId}`)}
                    className="group flex cursor-pointer items-center gap-2 text-sm font-bold uppercase tracking-widest text-foreground/50 transition-colors hover:text-accent"
                  >
                    <Boxes className="size-4" />
                    {group.name}
                    <span className="rounded-full border border-black/5 bg-black/5 px-2 py-0.5 text-[10px] font-bold normal-case tracking-normal text-foreground/40">
                      {group.items.length}
                    </span>
                  </button>
                  {renderGrid(group.items)}
                </section>
              ))}

              {groups.ungrouped.length > 0 && (
                <section className="space-y-4">
                  <p className="text-sm font-bold uppercase tracking-widest text-foreground/50">
                    Sin agrupar
                    <span className="ml-2 rounded-full border border-black/5 bg-black/5 px-2 py-0.5 text-[10px] font-bold normal-case tracking-normal text-foreground/40">
                      {groups.ungrouped.length}
                    </span>
                  </p>
                  {renderGrid(groups.ungrouped)}
                </section>
              )}
            </>
          )}
        </main>
      </div>

      {sharingAutomation && (
        <ShareAutomationModal
          automation={sharingAutomation}
          onClose={() => setSharingAutomation(null)}
        />
      )}

      {movingAutomation && (
        <MoveToWorkspaceModal
          automation={movingAutomation}
          onMoved={(workspace) => {
            // Reetiquetar en memoria en vez de recargar: el agrupado de la lista se recalcula solo.
            setAutomations((prev) =>
              prev.map((a) =>
                a.workflowId === movingAutomation.workflowId
                  ? {
                      ...a,
                      workspaceId: workspace?.id ?? null,
                      workspaceName: workspace?.name ?? null,
                    }
                  : a,
              ),
            );
            setMovingAutomation(null);
          }}
          onClose={() => setMovingAutomation(null)}
        />
      )}
    </GlassBackground>
  );
}
